import { useState, useEffect } from 'react';
import { getUserById } from '../api/user';
import { UserSubscriptionStatus } from '../types/user.types';

interface TrialData {
  status: UserSubscriptionStatus;
  daysRemaining: number;
  endDate: Date | null;
  isExpiringSoon: boolean;
  isExpired: boolean;
}

export const useTrialManager = () => {
  const [trialData, setTrialData] = useState<TrialData | null>(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [lastModalShown, setLastModalShown] = useState<string | null>(
    localStorage.getItem('lastTrialModalShown')
  );

  useEffect(() => {
    const checkTrialStatus = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) return;

        const user = await getUserById(parseInt(userId));

        let trialEndDate: Date | null = null;
        let daysRemaining = 0;

        // Calculate days remaining based on subscription data
        if (user.subscriptionStatus === UserSubscriptionStatus.TRIAL) {
          if (user.subscription?.trialEndDate) {
            // Use actual trial end date from subscription
            trialEndDate = new Date(user.subscription.trialEndDate);
            daysRemaining = Math.ceil(
              (trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            );
          } else {
            // Fallback: calculate from user creation date (7-day trial)
            const createdAt = new Date(user.createdAt);
            trialEndDate = new Date(createdAt);
            trialEndDate.setDate(trialEndDate.getDate() + 7);
            daysRemaining = Math.ceil(
              (trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            );
          }
          daysRemaining = Math.max(0, daysRemaining);
        }

        const isExpiringSoon =
          user.subscriptionStatus === UserSubscriptionStatus.TRIAL &&
          daysRemaining > 0 &&
          daysRemaining <= 3;
        const isExpired =
          user.subscriptionStatus === UserSubscriptionStatus.TRIAL_EXPIRED;

        setTrialData({
          status: user.subscriptionStatus,
          daysRemaining,
          endDate: trialEndDate,
          isExpiringSoon,
          isExpired,
        });

        // Show modal if trial is expiring soon or expired
        if (
          (isExpiringSoon || isExpired) &&
          (user.subscriptionStatus === UserSubscriptionStatus.TRIAL ||
            user.subscriptionStatus === UserSubscriptionStatus.TRIAL_EXPIRED)
        ) {
          const today = new Date().toDateString();

          // Only show modal once per day
          if (lastModalShown !== today) {
            setShowExpiryModal(true);
            setLastModalShown(today);
            localStorage.setItem('lastTrialModalShown', today);
          }
        }
      } catch (error) {
        console.error('Error checking trial status:', error);
      }
    };

    checkTrialStatus();

    // Check every hour
    const interval = setInterval(checkTrialStatus, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [lastModalShown]);

  return {
    trialData,
    showExpiryModal,
    setShowExpiryModal,
  };
};
